import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
    Badge,
    Button,
    Card,
    Checkbox,
    EmptyState,
    Notice,
    PageHeader,
    SelectInput,
    Spinner,
    TextArea,
    TextInput,
    linkStyles,
    useFormatters,
} from '@lumlab/platform-ui';
import {
    arbitrationView,
    arbitrationQueue,
    questionAction,
    type QuestionView,
    type ArbitrationView,
} from '../contract/analysis-arbitration.ts';
import type { AnalysisCommand } from '../contract/analysis-cases.ts';
import { AnalysisErrorAlert as ErrorAlert } from './AnalysisErrorAlert.tsx';
import { analysisCommand } from './analysis-api.ts';
import { QuestionForm } from './AnalysisQuestionForm.tsx';
import { AnalysisRunArtifacts } from './AnalysisRunArtifacts.tsx';
import { LearningRules } from './LearningRules.tsx';

function useAnalysisMutation() {
    const client = useQueryClient();
    return useMutation({
        mutationFn: analysisCommand,
        onSuccess: async () => {
            await client.invalidateQueries({ queryKey: ['analysis'] });
        },
    });
}
export function AnalysisArbitrationPage() {
    const { t } = useTranslation();
    const { runId } = useParams();
    return (
        <>
            <PageHeader title={t('tenders.arbitration.title')} description={t('tenders.arbitration.publicOnly')} />
            <Notice tone="warning">{t('tenders.arbitration.engineUnavailable')}</Notice>
            {runId ? (
                <RunArbitration key={runId} runId={runId} />
            ) : (
                <>
                    <ArbitrationQueue />
                    <LearningRules />
                </>
            )}
        </>
    );
}
function ArbitrationQueue() {
    const { t } = useTranslation();
    const [page, setPage] = useState(1);
    const query = useQuery({
        queryKey: ['analysis', 'queue', page],
        queryFn: async () => arbitrationQueue.parse(await analysisCommand({ operation: 'list-arbitrations', page })),
    });
    return (
        <>
            {query.isPending && <Spinner label={t('tenders.arbitration.loading')} />}
            {query.error && <ErrorAlert error={query.error} />}
            {query.data?.items.length === 0 && <EmptyState title={t('tenders.arbitration.empty')} />}
            {query.data?.items.map((item) => (
                <Card key={item.caseId} className="flex flex-wrap justify-between gap-3">
                    <Link className={linkStyles} to={`/admin/products/tenders/analysis/${item.runId}`}>
                        {item.title}
                    </Link>
                    <span>{t('tenders.arbitration.counts', { open: item.open, blocking: item.blocking })}</span>
                </Card>
            ))}
            <div className="flex gap-3">
                <Button variant="default" disabled={page === 1} onClick={() => setPage(page - 1)}>
                    {t('tenders.arbitration.previous')}
                </Button>
                <Button variant="default" disabled={!query.data?.hasMore} onClick={() => setPage(page + 1)}>
                    {t('tenders.arbitration.next')}
                </Button>
            </div>
        </>
    );
}
function RunArbitration({ runId }: { runId: string }) {
    const { t } = useTranslation();
    const format = useFormatters();
    const [creating, setCreating] = useState(false);
    const query = useQuery({
        queryKey: ['analysis', 'run', runId],
        queryFn: async () => arbitrationView.parse(await analysisCommand({ operation: 'get-arbitration', runId })),
    });
    const data = query.data;
    if (query.isPending) return <Spinner label={t('tenders.arbitration.loading')} />;
    if (query.error) return <ErrorAlert error={query.error} />;
    if (!data) return null;
    const previous = z.object({ input: z.object({ previousRunId: z.string().nullable() }) }).parse(data.run.payload)
        .input.previousRunId;
    return (
        <>
            <Link className={linkStyles} to="/admin/products/tenders/analysis">
                {t('tenders.arbitration.back')}
            </Link>
            <Card className="space-y-3">
                <h2>{data.title}</h2>
                <p className="break-all font-mono text-sm">{runId}</p>
                <Badge>
                    {data.purpose === 'validation'
                        ? t('tenders.arbitration.validation')
                        : t('tenders.arbitration.development')}
                </Badge>
                {previous && (
                    <p>
                        <Link className={linkStyles} to={`/admin/products/tenders/analysis/${previous}`}>
                            {t('tenders.arbitration.sourceRun')}
                        </Link>
                    </p>
                )}
                {!data.isCurrent && <Notice tone="warning">{t('tenders.arbitration.stale')}</Notice>}
                {data.resumptions.map((item) => (
                    <p key={item.id}>
                        <Link className={linkStyles} to={`/admin/products/tenders/analysis/${item.id}`}>
                            {t('tenders.arbitration.newRun')}
                        </Link>
                    </p>
                ))}
                <AnalysisRunArtifacts caseId={data.caseId} runId={runId} />
                <Button
                    variant="default"
                    onClick={() => {
                        void query.refetch();
                    }}
                >
                    {t('tenders.arbitration.refresh')}
                </Button>
            </Card>
            {data.questions.length === 0 && <EmptyState title={t('tenders.arbitration.noQuestions')} />}
            {data.decisions
                .filter(
                    (row) => !z.object({ facts: z.object({ questionId: z.string() }) }).safeParse(row.payload).success,
                )
                .map((row) => {
                    const decision = z
                        .object({ runId: z.uuid(), resolution: z.string(), justification: z.string() })
                        .parse(row.payload);
                    return (
                        <Card key={row.id} className="space-y-3">
                            <h3>{t('tenders.arbitration.standaloneDecision')}</h3>
                            <p>{decision.resolution}</p>
                            <p>{decision.justification}</p>
                            {decision.runId !== runId && (
                                <Link className={linkStyles} to={`/admin/products/tenders/analysis/${decision.runId}`}>
                                    {t('tenders.arbitration.ruleOrigin')}
                                </Link>
                            )}
                            <p className="break-all text-sm">
                                {row.actorId} · {format.dateTime(new Date(row.createdAt))}
                            </p>
                            {data.purpose === 'development' && <LearningRules decisionId={row.id} />}
                        </Card>
                    );
                })}
            {data.questions.map((question) => (
                <QuestionCard
                    key={question.question.id}
                    item={question}
                    editable={data.isCurrent}
                    purpose={data.purpose}
                />
            ))}
            {data.isCurrent && (
                <>
                    <Button variant="default" onClick={() => setCreating(!creating)}>
                        {t('tenders.arbitration.addQuestion')}
                    </Button>
                    {creating && <QuestionForm runId={runId} onDone={() => setCreating(false)} />}
                    <ResumeForm data={data} />
                </>
            )}
        </>
    );
}
export function QuestionCard({
    item,
    editable,
    purpose,
}: {
    item: QuestionView;
    editable: boolean;
    purpose: 'development' | 'validation';
}) {
    const { t } = useTranslation();
    const format = useFormatters();
    const [action, setAction] = useState<(typeof questionAction.options)[number]>(
        ['schema', 'rendering'].includes(item.question.payload.category) ? 'code_change' : 'answer',
    );
    const [resolution, setResolution] = useState('');
    const [justification, setJustification] = useState('');
    const [document, setDocument] = useState('');
    const [excerpt, setExcerpt] = useState('');
    const [page, setPage] = useState('');
    const [codeRevision, setCodeRevision] = useState('');
    const currentEventId = item.events.at(-1)?.id ?? null;
    const [reviewedEventId, setReviewedEventId] = useState(currentEventId);
    const stale = reviewedEventId !== currentEventId;
    const save = useAnalysisMutation();
    const question = item.question.payload;
    // Preserve an exact request after a network failure; changed content gets a new identity.
    const [pending, setPending] = useState<{ key: string; command: AnalysisCommand } | null>(null);
    const submit = () => {
        if (stale) return;
        const content = {
            operation: 'respond-question' as const,
            questionId: item.question.id,
            expectedEventId: reviewedEventId,
            action,
            ...(action === 'confirm_code_change' ? { codeRevision } : {}),
            resolution,
            justification,
            evidence: document && excerpt ? [{ document, excerpt, page: page ? Number(page) : null }] : [],
        };
        const key = JSON.stringify(content);
        const command = pending?.key === key ? pending.command : { ...content, id: crypto.randomUUID() };
        setPending({ key, command });
        save.mutate(command);
    };
    return (
        <Card className="space-y-4">
            <div className="flex flex-wrap gap-3">
                <Badge>{t(`tenders.arbitration.state.${item.state}`)}</Badge>
                <Badge>{t(`tenders.arbitration.category.${question.category}`)}</Badge>
                <Badge>{t(`tenders.arbitration.nature.${question.nature}`)}</Badge>
                <Badge>{t(`tenders.arbitration.importance.${question.importance}`)}</Badge>
                {question.blocking && <Badge tone="warning">{t('tenders.arbitration.blocking')}</Badge>}
            </div>
            <p className="break-all text-sm text-muted">
                {t('tenders.arbitration.author', {
                    id: item.question.actorId,
                    date: format.dateTime(new Date(item.question.createdAt)),
                })}
            </p>
            {question.carriedFrom && (
                <p>
                    <Link className={linkStyles} to={`/admin/products/tenders/analysis/${question.carriedFrom.runId}`}>
                        {t('tenders.arbitration.carriedQuestion')}
                    </Link>
                </p>
            )}
            <h3 className="font-semibold">{question.prompt}</h3>
            <p>{question.fields.join(', ')}</p>
            {question.evidence.map((source, index) => (
                <blockquote className="border-l-2 border-line pl-4" key={index}>
                    <p>
                        {source.document}
                        {source.page !== null && t('tenders.arbitration.page', { page: source.page })}
                    </p>
                    <p>{source.excerpt}</p>
                </blockquote>
            ))}
            {question.evidence.length === 0 && <p>{t('tenders.arbitration.noEvidence')}</p>}
            {question.alternatives.map((alternative, index) => (
                <p key={index}>{t('tenders.arbitration.alternative', { value: alternative })}</p>
            ))}
            <details>
                <summary>{t('tenders.arbitration.history')}</summary>
                <div className="space-y-3">
                    {item.events.map((event) => (
                        <div key={event.id} className="border-t border-line pt-3">
                            <p>
                                {format.dateTime(new Date(event.createdAt))} · {event.actorId}
                            </p>
                            <p>
                                {t(`tenders.arbitration.action.${event.payload.action}`)} · {event.payload.resolution}
                            </p>
                            <p>{event.payload.justification}</p>
                            {event.payload.codeRevision && (
                                <p className="break-all font-mono text-sm">{event.payload.codeRevision}</p>
                            )}
                            {event.payload.evidence.map((source, i) => (
                                <p key={i}>
                                    {source.document}
                                    {source.page !== null &&
                                        t('tenders.arbitration.page', { page: source.page })} · {source.excerpt}
                                </p>
                            ))}
                        </div>
                    ))}
                </div>
            </details>
            {editable && (
                <form
                    className="space-y-3"
                    onSubmit={(event) => {
                        event.preventDefault();
                        submit();
                    }}
                >
                    <SelectInput
                        label={t('tenders.arbitration.actionLabel')}
                        value={action}
                        onChange={(event) => setAction(questionAction.parse(event.target.value))}
                    >
                        {questionAction.options
                            .filter((option) =>
                                ['schema', 'rendering'].includes(question.category)
                                    ? !['answer', 'reject_out_of_scope'].includes(option)
                                    : option !== 'confirm_code_change',
                            )
                            .map((option) => (
                                <option key={option} value={option}>
                                    {t(`tenders.arbitration.action.${option}`)}
                                </option>
                            ))}
                    </SelectInput>
                    {stale && (
                        <Notice tone="warning">
                            {t('tenders.arbitration.draftStale')}
                            <Button
                                variant="default"
                                onClick={() => {
                                    setReviewedEventId(currentEventId);
                                    setPending(null);
                                    save.reset();
                                }}
                            >
                                {t('tenders.arbitration.reviewLatest')}
                            </Button>
                        </Notice>
                    )}
                    {action === 'confirm_code_change' && (
                        <TextInput
                            label={t('tenders.arbitration.codeRevision')}
                            value={codeRevision}
                            onChange={(event) => setCodeRevision(event.target.value)}
                            required
                            pattern="[a-f0-9]{40}"
                            maxLength={40}
                        />
                    )}
                    <TextArea
                        label={t('tenders.arbitration.resolution')}
                        value={resolution}
                        onChange={(event) => setResolution(event.target.value)}
                        required
                        maxLength={20000}
                    />
                    <TextArea
                        label={t('tenders.arbitration.justification')}
                        value={justification}
                        onChange={(event) => setJustification(event.target.value)}
                        required
                        maxLength={20000}
                    />
                    <TextInput
                        label={t('tenders.arbitration.document')}
                        value={document}
                        onChange={(event) => setDocument(event.target.value)}
                    />
                    <TextInput
                        label={t('tenders.arbitration.pageLabel')}
                        type="number"
                        min={1}
                        value={page}
                        onChange={(event) => setPage(event.target.value)}
                    />
                    <TextArea
                        label={t('tenders.arbitration.excerpt')}
                        value={excerpt}
                        onChange={(event) => setExcerpt(event.target.value)}
                        required={Boolean(document)}
                    />
                    {save.error && <ErrorAlert error={save.error} />}
                    {save.error && pending && (
                        <Button variant="default" loading={save.isPending} onClick={() => save.mutate(pending.command)}>
                            {t('tenders.arbitration.retryExact')}
                        </Button>
                    )}
                    <Button type="submit" variant="default" disabled={stale} loading={save.isPending}>
                        {t('tenders.arbitration.saveResponse')}
                    </Button>
                    {save.isSuccess && <Notice tone="success">{t('tenders.arbitration.responseSaved')}</Notice>}
                </form>
            )}
            {item.state === 'resolved' &&
                item.decisionId &&
                purpose === 'development' &&
                !['schema', 'rendering'].includes(question.category) && <LearningRules decisionId={item.decisionId} />}
        </Card>
    );
}
export function ResumeForm({ data }: { data: ArbitrationView }) {
    const { t } = useTranslation();
    const save = useAnalysisMutation();
    const [justification, setJustification] = useState('');
    const [reviewedStateHash, setReviewedStateHash] = useState(data.stateHash);
    const [confirmedHash, setConfirmedHash] = useState<string | null>(null);
    const stale = reviewedStateHash !== data.stateHash;
    const confirmed = !stale && confirmedHash === reviewedStateHash;
    const [pending, setPending] = useState<{ key: string; command: AnalysisCommand } | null>(null);
    const blocked = data.questions.some((q) => q.question.payload.blocking && q.state !== 'resolved');
    return (
        <Card>
            <form
                className="space-y-4"
                onSubmit={(event) => {
                    event.preventDefault();
                    if (stale || blocked || !confirmed) return;
                    const content = {
                        operation: 'resume-analysis' as const,
                        runId: data.run.id,
                        expectedStateHash: reviewedStateHash,
                        confirmBlockersReviewed: true as const,
                        justification,
                    };
                    const key = JSON.stringify(content);
                    const command = pending?.key === key ? pending.command : { ...content, id: crypto.randomUUID() };
                    setPending({ key, command });
                    save.mutate(command);
                }}
            >
                <h2>{t('tenders.arbitration.resume')}</h2>
                <TextArea
                    label={t('tenders.arbitration.justification')}
                    value={justification}
                    onChange={(event) => setJustification(event.target.value)}
                    required
                    maxLength={20000}
                />
                {stale && (
                    <Notice tone="warning">
                        {t('tenders.arbitration.draftStale')}
                        <Button
                            variant="default"
                            onClick={() => {
                                setReviewedStateHash(data.stateHash);
                                setConfirmedHash(null);
                                setPending(null);
                                save.reset();
                            }}
                        >
                            {t('tenders.arbitration.reviewLatest')}
                        </Button>
                    </Notice>
                )}
                <Checkbox
                    disabled={stale}
                    label={t('tenders.arbitration.confirmBlockers')}
                    checked={confirmed}
                    onChange={(event) => setConfirmedHash(event.target.checked ? reviewedStateHash : null)}
                />
                {blocked && <Notice tone="warning">{t('tenders.arbitration.blocked')}</Notice>}
                {save.error && <ErrorAlert error={save.error} />}
                {save.error && pending && (
                    <Button variant="default" loading={save.isPending} onClick={() => save.mutate(pending.command)}>
                        {t('tenders.arbitration.retryExact')}
                    </Button>
                )}
                <Button type="submit" disabled={blocked || !confirmed} loading={save.isPending}>
                    {t('tenders.arbitration.prepareResume')}
                </Button>
            </form>
        </Card>
    );
}
